import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../../../contexts/DataContext';
import { useAuth } from '../../../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiX, FiMail, FiShield, FiSend, FiBriefcase, FiUser, FiTag, FiCheckCircle } = FiIcons;

const InviteUserModal = ({ isOpen, onClose }) => {
  const { addUser, departments = [] } = useData();
  const { user: currentUser } = useAuth();
  
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [role, setRole] = useState('user');
  const [departmentId, setDepartmentId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Filtrer avdelinger for gjeldende bedrift
  const companyDepts = Array.isArray(departments) 
    ? departments.filter(d => d.companyId === currentUser?.companyId || d.company_id === currentUser?.companyId) 
    : [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Kall addUser fra context
    const result = await addUser({
        email,
        name,
        role: role || 'user',
        departmentId, // Kobler brukeren til valgt avdeling
        companyId: currentUser?.companyId,
        jobTitle: title
    });

    setIsSubmitting(false);
    
    if (result.success) {
        setSuccess(true);
        // Reset form after delay
        setTimeout(() => {
            setSuccess(false);
            onClose();
            setEmail('');
            setName('');
            setTitle('');
            setRole('user');
            setDepartmentId('');
        }, 1500);
    } else {
        alert("Kunne ikke invitere bruker: " + result.error);
    }
  };

  if (!isOpen) return null;

  // Mørk tema-stil basert på bildet
  const inputContainerStyle = "relative flex items-center bg-[#2d3748] border border-[#4a5568] rounded-lg overflow-hidden focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all";
  const inputStyle = "w-full py-3 px-3 bg-[#2d3748] text-white text-sm outline-none placeholder-gray-400";
  const iconStyle = "pl-3 text-gray-400 text-lg";
  const labelStyle = "block text-xs font-medium text-gray-300 mb-1.5";

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }} 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" 
        onClick={onClose}
      >
        <motion.div 
          initial={{ scale: 0.95, opacity: 0 }} 
          animate={{ scale: 1, opacity: 1 }} 
          exit={{ scale: 0.95, opacity: 0 }} 
          className="bg-[#1a202c] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-700" 
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-700 flex justify-between items-center">
            <h2 className="text-lg font-bold text-white">Inviter ny bruker</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors" type="button">
              <SafeIcon icon={FiX} />
            </button>
          </div>

          {/* Success State */}
          {success ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4 text-green-400">
                <SafeIcon icon={FiCheckCircle} className="text-3xl" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Invitasjon sendt!</h3>
              <p className="text-gray-400">Brukeren er lagt til i systemet.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              
              {/* Navn */}
              <div>
                <label className={labelStyle}>Navn</label>
                <div className={inputContainerStyle}>
                  <span className={iconStyle}><SafeIcon icon={FiUser} /></span>
                  <input 
                    type="text" 
                    required 
                    value={name} 
                    onChange={(e) => setName(e.target.value)} 
                    className={inputStyle} 
                    placeholder="Ola Nordmann" 
                  />
                </div>
              </div>

              {/* E-post */}
              <div>
                <label className={labelStyle}>E-postadresse *</label>
                <div className={inputContainerStyle}>
                  <span className={iconStyle}><SafeIcon icon={FiMail} /></span>
                  <input 
                    type="email" 
                    required 
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                    className={inputStyle} 
                    placeholder="navn@bedrift.no" 
                  />
                </div>
              </div>

              {/* Stillingstittel (NYTT FELT) */}
              <div>
                <label className={labelStyle}>Stillingstittel</label>
                <div className={inputContainerStyle}>
                  <span className={iconStyle}><SafeIcon icon={FiTag} /></span>
                  <input 
                    type="text" 
                    value={title} 
                    onChange={(e) => setTitle(e.target.value)} 
                    className={inputStyle} 
                    placeholder="F.eks. Salgssjef" 
                  />
                </div>
              </div>

              {/* Avdeling (NYTT FELT) */}
              <div>
                <label className={labelStyle}>Avdeling</label>
                <div className={inputContainerStyle}>
                  <span className={iconStyle}><SafeIcon icon={FiBriefcase} /></span>
                  <select 
                    value={departmentId} 
                    onChange={(e) => setDepartmentId(e.target.value)} 
                    className={`${inputStyle} bg-transparent appearance-none cursor-pointer`}
                  >
                    <option value="" className="text-gray-500 bg-[#2d3748]">Velg avdeling...</option>
                    {companyDepts.map(dept => (
                        <option key={dept.id} value={dept.id} className="bg-[#2d3748] text-white">
                            {dept.name}
                        </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Rolle */}
              <div>
                <label className={labelStyle}>Rolle / Tilgangsnivå</label>
                <div className={inputContainerStyle}>
                  <span className={iconStyle}><SafeIcon icon={FiShield} /></span>
                  <select 
                    value={role} 
                    onChange={(e) => setRole(e.target.value)} 
                    className={`${inputStyle} bg-transparent appearance-none cursor-pointer`}
                  >
                    <option value="user" className="bg-[#2d3748]">Bruker (Standard)</option>
                    <option value="admin" className="bg-[#2d3748]">Administrator</option>
                    <option value="safety_representative" className="bg-[#2d3748]">Verneombud</option>
                    <option value="hms_manager" className="bg-[#2d3748]">HMS ansvarlig</option>
                  </select>
                </div>
              </div>

              <div className="pt-4 flex justify-end gap-3 mt-8">
                <button 
                    type="button" 
                    onClick={onClose} 
                    className="px-5 py-2.5 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors font-medium text-sm"
                >
                    Avbryt
                </button>
                <button 
                    type="submit" 
                    disabled={isSubmitting} 
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors flex items-center gap-2 font-medium text-sm shadow-lg shadow-blue-500/20"
                >
                    {isSubmitting ? <SafeIcon icon={FiSend} className="animate-spin" /> : <SafeIcon icon={FiSend} />}
                    <span>Send Invitasjon</span>
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InviteUserModal;